import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import AppImage from '../../components/AppImage';
import EmptyState from '../../components/ui/EmptyState';
import { wishlistService } from '../../services/wishlistService';
import { getUserAppointments } from '../../services/appointmentService';
import { getPropertyInquiries } from '../../services/propertyInquiryService';
import { roiCalculatorService } from '../../services/roiCalculatorService';
import { subscribePooled } from '../../utils/realtimePoolManager';
import { saveOfflineData, getOfflineData, isOffline, onConnectivityChange } from '../../utils/offlineStorage';
import UserTransactionDashboard from './components/UserTransactionDashboard';
import SellerSpaceDashboard from './components/SellerSpaceDashboard';
import EscrowWalkthrough from './components/EscrowWalkthrough';

// ─── Realtime Toast System ────────────────────────────────────────────────────

const TOAST_META = {
  // Appointment status changes
  confirmed:   { icon: '✅', label: 'Rendez-vous confirmé',     bg: 'bg-green-50 border-green-200',  text: 'text-green-800' },
  rescheduled: { icon: '🔄', label: 'Rendez-vous reprogrammé',  bg: 'bg-amber-50 border-amber-200',  text: 'text-amber-800' },
  cancelled:   { icon: '❌', label: 'Rendez-vous annulé',       bg: 'bg-red-50 border-red-200',      text: 'text-red-800' },
  completed:   { icon: '🏁', label: 'Rendez-vous terminé',      bg: 'bg-blue-50 border-blue-200',    text: 'text-blue-800' },
  // New inquiry lead
  new_inquiry: { icon: '📩', label: 'Nouvelle demande reçue',   bg: 'bg-brand-gold/10 border-brand-gold/30', text: 'text-brand-earth' },
};

const useRealtimeToasts = () => {
  const [toasts, setToasts] = useState([]);
  const timerRefs = useRef({});

  const addToast = useCallback((type, subtitle = '') => {
    const id = `rt-${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev, { id, type, subtitle }]?.slice(-4));
    const timer = setTimeout(() => {
      setToasts((prev) => prev?.filter((t) => t?.id !== id));
      delete timerRefs?.current?.[id];
    }, 5500);
    timerRefs.current[id] = timer;
  }, []);

  const dismiss = useCallback((id) => {
    if (timerRefs?.current?.[id]) {
      clearTimeout(timerRefs?.current?.[id]);
      delete timerRefs?.current?.[id];
    }
    setToasts((prev) => prev?.filter((t) => t?.id !== id));
  }, []);

  useEffect(() => () => Object.values(timerRefs?.current ?? {})?.forEach(clearTimeout), []);

  return { toasts, addToast, dismiss };
};

const RealtimeToastStack = ({ toasts, onDismiss }) => {
  if (!toasts?.length) return null;
  return (
    <div className="fixed bottom-6 right-4 z-50 flex flex-col gap-2 max-w-xs w-full pointer-events-none">
      {toasts?.map((t) => {
        const meta = TOAST_META?.[t?.type] || { icon: '🔔', label: 'Mise à jour', bg: 'bg-white border-border', text: 'text-text-primary' };
        return (
          <div
            key={t?.id}
            className={`flex items-start gap-3 px-4 py-3 rounded-2xl border shadow-ios-medium pointer-events-auto ${meta?.bg}`}
            style={{ animation: 'slideInRight 0.25s ease-out' }}
          >
            <span className="text-base leading-none mt-0.5 flex-shrink-0">{meta?.icon}</span>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${meta?.text}`}>{meta?.label}</p>
              {t?.subtitle && <p className="text-xs text-text-secondary truncate mt-0.5">{t?.subtitle}</p>}
            </div>
            <button
              onClick={() => onDismiss?.(t?.id)}
              className="text-text-secondary hover:text-text-primary transition-colors flex-shrink-0"
            >
              <Icon name="X" size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatPrice = (price) => {
  if (!price) return '—';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'XOF',
    maximumFractionDigits: 0,
  })?.format(price);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })?.format(new Date(dateStr));
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })?.format(new Date(dateStr));
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionCard = ({ icon, iconBg = 'bg-brand-earth/10', iconColor = 'text-brand-earth', title, subtitle, count, children, ctaPath, ctaLabel, isLive = false }) => (
  <div className="bg-white dark:bg-card rounded-2xl shadow-ios-light border border-border/50 overflow-hidden">
    <div className="flex items-center justify-between px-5 py-4 border-b border-border/40">
      <div className="flex items-center space-x-3">
        <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
          <Icon name={icon} size={18} className={iconColor} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="text-ios-callout font-body font-semibold text-text-primary">{title}</p>
            {count !== undefined && count > 0 && (
              <span className="px-2 py-0.5 bg-brand-gold/15 text-brand-gold text-[11px] font-bold rounded-full">
                {count}
              </span>
            )}
            {isLive && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[10px] font-semibold text-emerald-600">Live</span>
              </span>
            )}
          </div>
          {subtitle && <p className="text-ios-caption1 text-text-secondary mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {ctaPath && ctaLabel && (
        <Link
          to={ctaPath}
          className="flex items-center gap-1 text-xs font-medium text-brand-earth hover:text-brand-earth/80 transition-colors"
        >
          <span>{ctaLabel}</span>
          <Icon name="ArrowRight" size={12} />
        </Link>
      )}
    </div>
    <div className="px-5 py-5">{children}</div>
  </div>
);

const StatusBadge = ({ status }) => {
  const config = {
    scheduled: { label: 'Planifié', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-100' },
    confirmed: { label: 'Confirmé', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-100' },
    completed: { label: 'Terminé', bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' },
    cancelled: { label: 'Annulé', bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-100' },
    rescheduled: { label: 'Reprogrammé', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-100' },
    new: { label: 'Nouveau', bg: 'bg-brand-gold/10', text: 'text-brand-gold', border: 'border-brand-gold/20' },
    pending: { label: 'En attente', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-100' },
    responded: { label: 'Répondu', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-100' },
    closed: { label: 'Clôturé', bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' },
    saved: { label: 'Sauvegardé', bg: 'bg-brand-earth/10', text: 'text-brand-earth', border: 'border-brand-earth/20' },
  };
  const cfg = config?.[status] || config?.new;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-[11px] font-semibold border ${cfg?.bg} ${cfg?.text} ${cfg?.border}`}>
      {cfg?.label}
    </span>
  );
};

const LoadingSkeleton = ({ rows = 3 }) => (
  <div className="space-y-3">
    {Array.from({ length: rows })?.map((_, i) => (
      <div key={i} className="animate-pulse flex items-center gap-3 p-3 rounded-xl bg-surface/60">
        <div className="w-12 h-12 rounded-xl bg-border/50 flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-3.5 bg-border/50 rounded-lg w-3/4" />
          <div className="h-3 bg-border/40 rounded-lg w-1/2" />
        </div>
        <div className="h-5 w-16 bg-border/40 rounded-lg" />
      </div>
    ))}
  </div>
);

// ─── Saved Properties Section ─────────────────────────────────────────────────

const SavedPropertiesSection = ({ userId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLive, setIsLive] = useState(false);
  const [offline, setOffline] = useState(isOffline());

  const fetchWishlist = useCallback(async () => {
    if (!userId) return;
    if (isOffline()) {
      const cached = await getOfflineData(`wishlist_${userId}`);
      if (cached) setItems(cached);
      return;
    }
    const { data } = await wishlistService?.getWishlist(userId, { sortOrder: 'desc' });
    const list = data?.slice(0, 6) || [];
    setItems(list);
    // Cache for offline use
    saveOfflineData(`wishlist_${userId}`, list);
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      setLoading(true);
      await fetchWishlist();
      setLoading(false);
    })();

    const unsubConnectivity = onConnectivityChange((online) => {
      setOffline(!online);
      if (online) fetchWishlist();
    });

    const unsubscribe = subscribePooled({
      channelName: `mon-espace-wishlist-${userId}`,
      listeners: [
        { event: '*', schema: 'public', table: 'user_wishlist', filter: `user_id=eq.${userId}` },
      ],
      debounceMs: 350,
      onStatusChange: (status) => setIsLive(status === 'SUBSCRIBED'),
      callback: () => { fetchWishlist(); },
    });

    return () => { unsubscribe(); unsubConnectivity(); };
  }, [userId, fetchWishlist]);

  return (
    <SectionCard
      icon="Heart"
      iconBg="bg-rose-50"
      iconColor="text-rose-500"
      title="Propriétés sauvegardées"
      subtitle={offline ? '📶 Mode hors-ligne — données en cache' : 'Votre liste de favoris'}
      count={items?.length}
      ctaPath="/investor-watchlist"
      ctaLabel="Voir tout"
      isLive={isLive && !offline}
    >
      {loading ? (
        <LoadingSkeleton rows={3} />
      ) : items?.length === 0 ? (
        <EmptyState
          iconName="Heart"
          title="Aucune propriété sauvegardée"
          description="Explorez nos propriétés et ajoutez vos favoris pour les retrouver ici."
          ctaLabel="Explorer les propriétés"
          ctaIcon="Search"
          onCta={() => window.location.href = '/property-search-listings'}
          size="sm"
        />
      ) : (
        <div className="space-y-2.5">
          {items?.map((item) => (
            <Link
              key={item?.id}
              to={`/individual-property-page?id=${item?.property_id}`}
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-surface/70 transition-colors duration-150 group"
            >
              <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 bg-border/30">
                {item?.property_image ? (
                  <AppImage
                    src={item?.property_image}
                    alt={item?.property_title || 'Propriété'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-brand-earth/10">
                    <Icon name="Building2" size={18} className="text-brand-earth/60" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-text-primary truncate group-hover:text-brand-earth transition-colors">
                  {item?.property_title || 'Propriété sans titre'}
                </p>
                <p className="text-xs text-text-secondary truncate mt-0.5">
                  {item?.property_commune || '—'} · {item?.property_type || '—'}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className="text-sm font-bold text-brand-earth">
                  {formatPrice(item?.property_price)}
                </span>
                <span className="text-[10px] text-text-secondary">
                  {formatDate(item?.created_at)}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </SectionCard>
  );
};

// ─── Upcoming Appointments Section ────────────────────────────────────────────

const UpcomingAppointmentsSection = ({ userId, addToast }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLive, setIsLive] = useState(false);
  const [offline, setOffline] = useState(isOffline());
  const prevStatusMap = useRef({});

  const fetchAppointments = useCallback(async () => {
    if (!userId) return;
    if (isOffline()) {
      const cached = await getOfflineData(`appointments_${userId}`);
      if (cached) setItems(cached);
      return;
    }
    const { data } = await getUserAppointments();
    const now = new Date();
    const upcoming = (data || [])
      ?.filter(a => {
        const d = new Date(a?.scheduled_date);
        return d >= now && ['scheduled', 'confirmed']?.includes(a?.status);
      })
      ?.sort((a, b) => new Date(a?.scheduled_date) - new Date(b?.scheduled_date))
      ?.slice(0, 5);
    setItems(upcoming);
    // Update status map for change detection
    const newMap = {};
    (data || [])?.forEach((a) => { newMap[a?.id] = a?.status; });
    prevStatusMap.current = newMap;
    saveOfflineData(`appointments_${userId}`, upcoming);
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      setLoading(true);
      await fetchAppointments();
      setLoading(false);
    })();

    const unsubConnectivity = onConnectivityChange((online) => {
      setOffline(!online);
      if (online) fetchAppointments();
    });

    const unsubscribe = subscribePooled({
      channelName: `mon-espace-appointments-${userId}`,
      listeners: [
        { event: '*', schema: 'public', table: 'appointments' },
      ],
      debounceMs: 350,
      onStatusChange: (status) => setIsLive(status === 'SUBSCRIBED'),
      callback: (batch) => {
        // Detect status changes and fire toasts
        batch?.forEach((payload) => {
          if (payload?.eventType === 'UPDATE' && payload?.new) {
            const prev = prevStatusMap?.current?.[payload?.new?.id];
            const next = payload?.new?.status;
            if (prev && next && prev !== next && TOAST_META?.[next]) {
              addToast?.(next, payload?.new?.appointment_type || '');
            }
          }
        });

        const relevant = batch?.some((payload) => {
          const record = payload?.new || payload?.old;
          return !record?.user_id || record?.user_id === userId;
        });
        if (relevant) fetchAppointments();
      },
    });

    return () => { unsubscribe(); unsubConnectivity(); };
  }, [userId, fetchAppointments, addToast]);

  return (
    <SectionCard
      icon="CalendarCheck"
      iconBg="bg-blue-50"
      iconColor="text-blue-600"
      title="Rendez-vous à venir"
      subtitle={offline ? '📶 Mode hors-ligne — données en cache' : 'Vos prochains rendez-vous planifiés'}
      count={items?.length}
      ctaPath="/user-appointment-management"
      ctaLabel="Gérer"
      isLive={isLive && !offline}
    >
      {loading ? (
        <LoadingSkeleton rows={3} />
      ) : items?.length === 0 ? (
        <EmptyState
          iconName="CalendarCheck"
          title="Aucun rendez-vous à venir"
          description="Planifiez une visite ou une consultation avec un notaire."
          ctaLabel="Prendre rendez-vous"
          ctaIcon="Calendar"
          onCta={() => window.location.href = '/notary-appointment-booking-system'}
          size="sm"
        />
      ) : (
        <div className="space-y-2.5">
          {items?.map((appt) => (
            <div
              key={appt?.id}
              className="flex items-start gap-3 p-3 rounded-xl bg-surface/50 border border-border/30"
            >
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Icon name="Calendar" size={16} className="text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-text-primary truncate">
                  {appt?.property?.title || appt?.notary?.notary_name || 'Rendez-vous'}
                </p>
                <p className="text-xs text-text-secondary mt-0.5">
                  {formatDateTime(appt?.scheduled_date)}
                </p>
                {appt?.property?.address && (
                  <p className="text-xs text-text-secondary truncate mt-0.5 flex items-center gap-1">
                    <Icon name="MapPin" size={10} />
                    {appt?.property?.address}
                  </p>
                )}
              </div>
              <StatusBadge status={appt?.status} />
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
};

// ─── Past Inquiries Section ───────────────────────────────────────────────────

const PastInquiriesSection = ({ userId, addToast }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLive, setIsLive] = useState(false);

  const fetchInquiries = useCallback(async () => {
    if (!userId) return;
    const { data } = await getPropertyInquiries();
    const userInquiries = (data || [])?.filter(inq => inq?.client_id === userId || !inq?.client_id)?.slice(0, 5);
    setItems(userInquiries);
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      setLoading(true);
      await fetchInquiries();
      setLoading(false);
    })();

    const unsubscribe = subscribePooled({
      channelName: `mon-espace-inquiries-${userId}`,
      listeners: [
        { event: '*', schema: 'public', table: 'property_inquiries' },
      ],
      debounceMs: 350,
      onStatusChange: (status) => setIsLive(status === 'SUBSCRIBED'),
      callback: (batch) => {
        // Toast for new incoming leads
        batch?.forEach((payload) => {
          if (payload?.eventType === 'INSERT' && payload?.new) {
            addToast?.('new_inquiry', payload?.new?.message?.slice(0, 60) || '');
          }
        });

        const relevant = batch?.some((payload) => {
          const record = payload?.new || payload?.old;
          return !record?.client_id || record?.client_id === userId;
        });
        if (relevant) fetchInquiries();
      },
    });

    return () => { unsubscribe(); };
  }, [userId, fetchInquiries, addToast]);

  return (
    <SectionCard
      icon="MessageSquare"
      iconBg="bg-brand-gold/10"
      iconColor="text-brand-gold"
      title="Demandes passées"
      subtitle="Historique de vos demandes d'information"
      count={items?.length}
      ctaPath="/activity-history"
      ctaLabel="Historique"
      isLive={isLive}
    >
      {loading ? (
        <LoadingSkeleton rows={3} />
      ) : items?.length === 0 ? (
        <EmptyState
          iconName="MessageSquare"
          title="Aucune demande passée"
          description="Vos demandes d'information sur les propriétés apparaîtront ici."
          ctaLabel="Voir les propriétés"
          ctaIcon="Home"
          onCta={() => window.location.href = '/property-search-listings'}
          size="sm"
        />
      ) : (
        <div className="space-y-2.5">
          {items?.map((inq) => (
            <div
              key={inq?.id}
              className="flex items-start gap-3 p-3 rounded-xl bg-surface/50 border border-border/30"
            >
              <div className="w-10 h-10 rounded-xl bg-brand-gold/10 flex items-center justify-center flex-shrink-0">
                <Icon name="FileText" size={16} className="text-brand-gold" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-text-primary line-clamp-2">
                  {inq?.message?.length > 80 ? inq?.message?.slice(0, 80) + '…' : inq?.message || 'Demande'}
                </p>
                {inq?.property?.title && (
                  <p className="text-xs text-brand-earth font-medium mt-0.5 truncate">
                    {inq?.property?.title}
                  </p>
                )}
                <p className="text-xs text-text-secondary mt-0.5">
                  {formatDate(inq?.created_at)}
                </p>
              </div>
              <StatusBadge status={inq?.status || 'new'} />
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
};

// ─── Investment Calculations Section ─────────────────────────────────────────

const InvestmentCalculationsSection = ({ userId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      setLoading(true);
      try {
        const data = await roiCalculatorService?.getUserCalculations(userId);
        setItems((data || [])?.slice(0, 5));
      } catch (err) {
        setError('Impossible de charger les calculs.');
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  return (
    <SectionCard
      icon="TrendingUp"
      iconBg="bg-emerald-50"
      iconColor="text-emerald-600"
      title="Calculs d'investissement"
      subtitle="Vos simulations ROI sauvegardées"
      count={items?.length}
      ctaPath="/roi-calculator-tool"
      ctaLabel="Nouveau calcul"
    >
      {loading ? (
        <LoadingSkeleton rows={3} />
      ) : error ? (
        <EmptyState
          iconName="AlertCircle"
          title="Erreur de chargement"
          description={error}
          ctaLabel="Réessayer"
          ctaIcon="RefreshCw"
          onCta={() => window.location?.reload()}
          size="sm"
        />
      ) : items?.length === 0 ? (
        <EmptyState
          iconName="Calculator"
          title="Aucun calcul sauvegardé"
          description="Utilisez notre calculateur ROI pour simuler vos investissements immobiliers."
          ctaLabel="Calculer le ROI"
          ctaIcon="TrendingUp"
          onCta={() => window.location.href = '/roi-calculator-tool'}
          size="sm"
        />
      ) : (
        <div className="space-y-2.5">
          {items?.map((calc) => (
            <Link
              key={calc?.id}
              to="/roi-calculator-tool"
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-surface/70 transition-colors duration-150 group"
            >
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <Icon name="BarChart2" size={16} className="text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-text-primary truncate group-hover:text-brand-earth transition-colors">
                  {calc?.calculation_name || calc?.property?.title || 'Simulation ROI'}
                </p>
                <p className="text-xs text-text-secondary mt-0.5">
                  {formatPrice(calc?.property_price)} · {formatDate(calc?.created_at)}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                {calc?.total_roi != null && (
                  <span className={`text-sm font-bold ${calc?.total_roi >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {calc?.total_roi >= 0 ? '+' : ''}{Number(calc?.total_roi)?.toFixed(1)}%
                  </span>
                )}
                {calc?.cap_rate != null && (
                  <span className="text-[10px] text-text-secondary">
                    Cap: {Number(calc?.cap_rate)?.toFixed(1)}%
                  </span>
                )}
                <StatusBadge status={calc?.status || 'saved'} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </SectionCard>
  );
};

// ─── Summary Stats Bar ────────────────────────────────────────────────────────

const StatItem = ({ icon, label, value, color = 'text-brand-earth' }) => (
  <div className="flex flex-col items-center gap-1 px-4 py-3">
    <div className={`w-8 h-8 rounded-xl flex items-center justify-center bg-current/10`} style={{ backgroundColor: 'transparent' }}>
      <Icon name={icon} size={18} className={color} />
    </div>
    <span className={`text-xl font-bold font-heading ${color}`}>{value}</span>
    <span className="text-xs text-text-secondary text-center leading-tight">{label}</span>
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const MonEspace = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toasts, addToast, dismiss } = useRealtimeToasts();

  const [stats, setStats] = useState({ wishlist: 0, appointments: 0, inquiries: 0, calculations: 0 });
  const [statsLoading, setStatsLoading] = useState(true);
  const [showWalkthrough, setShowWalkthrough] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      setStatsLoading(true);
      try {
        const [wishlistRes, appointmentsRes, inquiriesRes, calcData] = await Promise.allSettled([
          wishlistService?.getWishlist(user?.id),
          getUserAppointments(),
          getPropertyInquiries(),
          roiCalculatorService?.getUserCalculations(user?.id),
        ]);

        const wishlistCount = wishlistRes?.status === 'fulfilled' ? (wishlistRes?.value?.data?.length || 0) : 0;
        const now = new Date();
        const upcomingCount = appointmentsRes?.status === 'fulfilled'
          ? (appointmentsRes?.value?.data || [])?.filter(a => new Date(a?.scheduled_date) >= now && ['scheduled', 'confirmed']?.includes(a?.status))?.length
          : 0;
        const inquiriesCount = inquiriesRes?.status === 'fulfilled'
          ? (inquiriesRes?.value?.data || [])?.filter(i => i?.client_id === user?.id || !i?.client_id)?.length
          : 0;
        const calcCount = calcData?.status === 'fulfilled' ? (calcData?.value?.length || 0) : 0;

        setStats({ wishlist: wishlistCount, appointments: upcomingCount, inquiries: inquiriesCount, calculations: calcCount });
      } catch (_) {}
      setStatsLoading(false);
    })();
  }, [user?.id]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  const displayName = userProfile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || 'Utilisateur';

  return (
    <>
      <Helmet>
        <title>Mon Espace — Kapital States</title>
        <meta name="description" content="Gérez vos propriétés sauvegardées, rendez-vous, demandes et calculs d'investissement." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        {/* Realtime toast stack */}
        <RealtimeToastStack toasts={toasts} onDismiss={dismiss} />

        {/* Page Header */}
        <div className="bg-gradient-to-br from-brand-earth via-brand-earth/90 to-brand-navy px-6 py-8 md:py-10">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-white/15 border border-white/25 flex items-center justify-center flex-shrink-0">
                <Icon name="LayoutGrid" size={26} color="white" strokeWidth={1.75} />
              </div>
              <div>
                <p className="text-white/70 text-sm font-body mb-0.5">Bienvenue,</p>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-white leading-tight">
                  {displayName}
                </h1>
              </div>
            </div>

            {/* Stats Bar */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { icon: 'Heart', label: 'Favoris', value: statsLoading ? '…' : stats?.wishlist, color: 'text-rose-300' },
                { icon: 'CalendarCheck', label: 'Rendez-vous', value: statsLoading ? '…' : stats?.appointments, color: 'text-blue-300' },
                { icon: 'MessageSquare', label: 'Demandes', value: statsLoading ? '…' : stats?.inquiries, color: 'text-brand-gold' },
                { icon: 'TrendingUp', label: 'Simulations ROI', value: statsLoading ? '…' : stats?.calculations, color: 'text-emerald-300' },
              ]?.map((stat) => (
                <div key={stat?.label} className="bg-white/10 border border-white/15 rounded-2xl flex flex-col items-center py-3 px-2">
                  <Icon name={stat?.icon} size={18} className={stat?.color} />
                  <span className={`text-2xl font-bold font-heading mt-1 ${stat?.color}`}>{stat?.value}</span>
                  <span className="text-white/60 text-xs mt-0.5 text-center">{stat?.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 space-y-6">

          {/* Quick Actions */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { icon: 'Search', label: 'Chercher', sub: 'Propriétés', path: '/property-search-listings', bg: 'bg-brand-earth/10', color: 'text-brand-earth' },
              { icon: 'Calendar', label: 'Réserver', sub: 'Rendez-vous', path: '/notary-appointment-booking-system', bg: 'bg-blue-50', color: 'text-blue-600' },
              { icon: 'Calculator', label: 'Calculer', sub: 'ROI', path: '/roi-calculator-tool', bg: 'bg-emerald-50', color: 'text-emerald-600' },
              { icon: 'BarChart2', label: 'Analyser', sub: 'Le marché', path: '/market-analysis-dashboard', bg: 'bg-brand-gold/10', color: 'text-brand-gold' },
            ]?.map((action) => (
              <Link
                key={action?.path}
                to={action?.path}
                className="flex flex-col items-center gap-2 p-4 bg-white dark:bg-card rounded-2xl border border-border/50 shadow-ios-light hover:shadow-ios-medium hover:-translate-y-0.5 transition-all duration-200 group"
              >
                <div className={`w-10 h-10 rounded-xl ${action?.bg} flex items-center justify-center`}>
                  <Icon name={action?.icon} size={18} className={action?.color} />
                </div>
                <div className="text-center">
                  <p className="text-xs font-bold text-text-primary group-hover:text-brand-earth transition-colors">{action?.label}</p>
                  <p className="text-[10px] text-text-secondary">{action?.sub}</p>
                </div>
              </Link>
            ))}
          </div>

          {/* Walkthrough CTA */}
          {!showWalkthrough && (
            <button
              onClick={() => setShowWalkthrough(true)}
              className="w-full flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-brand-earth/8 to-brand-navy/8 border border-brand-earth/20 rounded-2xl hover:border-brand-earth/40 hover:shadow-ios-light transition-all duration-200 group"
            >
              <div className="w-10 h-10 rounded-xl bg-brand-earth/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Map" size={18} className="text-brand-earth" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-semibold text-text-primary group-hover:text-brand-earth transition-colors">
                  Nouveau ? Découvrez le workflow séquestre
                </p>
                <p className="text-xs text-text-secondary mt-0.5">
                  Guide interactif : réservation → paiement → séquestre → litige → libération
                </p>
              </div>
              <Icon name="ChevronRight" size={16} className="text-brand-earth flex-shrink-0" />
            </button>
          )}

          {/* Escrow Workflow Walkthrough */}
          {showWalkthrough && (
            <EscrowWalkthrough onClose={() => setShowWalkthrough(false)} />
          )}

          {/* Main Grid — 2 columns on desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SavedPropertiesSection userId={user?.id} />
            <UpcomingAppointmentsSection userId={user?.id} addToast={addToast} />
            <PastInquiriesSection userId={user?.id} addToast={addToast} />
            <InvestmentCalculationsSection userId={user?.id} />
          </div>

          {/* Transaction Dashboard */}
          <UserTransactionDashboard userId={user?.id} />

          {/* Seller Space Dashboard */}
          <div className="bg-white dark:bg-card rounded-2xl shadow-ios-light border border-border/50 overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-border/40">
              <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Icon name="BarChart3" size={18} className="text-blue-600" />
              </div>
              <div>
                <p className="text-ios-callout font-body font-semibold text-text-primary">Mon Espace Vendeur</p>
                <p className="text-ios-caption1 text-text-secondary mt-0.5">Suivi de vos ventes, séquestre et commissions</p>
              </div>
            </div>
            <div className="px-5 py-5">
              <SellerSpaceDashboard userId={user?.id} />
            </div>
          </div>

          {/* Footer Links */}
          <div className="flex flex-wrap items-center justify-center gap-4 pt-4 pb-8 border-t border-border/40">
            {[
              { label: 'Mon Profil', path: '/user-profile', icon: 'UserCircle' },
              { label: 'Mon Compte', path: '/account-dashboard', icon: 'Settings' },
              { label: 'Notifications', path: '/notifications', icon: 'Bell' },
              { label: 'Messagerie', path: '/in-app-messaging', icon: 'MessageSquare' },
              { label: 'Historique', path: '/activity-history', icon: 'ClipboardList' },
            ]?.map((link) => (
              <Link
                key={link?.path}
                to={link?.path}
                className="flex items-center gap-1.5 text-xs text-text-secondary hover:text-brand-earth transition-colors"
              >
                <Icon name={link?.icon} size={13} />
                <span>{link?.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default MonEspace;
